/**
 * AnalyticsOverview — the studio's lifetime pulse, four tiles.
 *
 * POL-16 redesign. Dropped the generic `.stat-card` + primary-tint
 * icon plate (read as shadcn starter chrome). Now matches the rest
 * of the redesigned Today: editorial section header (eyebrow +
 * Fraunces title), ink-gradient tile surfaces, and one Pixieset
 * pastel per metric so each number has its own identity.
 */
import { AnalyticsData } from '@/types/crm';
import { BarChart3, CalendarDays, TrendingUp, Play } from 'lucide-react';

interface AnalyticsOverviewProps {
  analytics: AnalyticsData;
}

const analyticsCards: {
  key: keyof AnalyticsData;
  label: string;
  icon: typeof BarChart3;
  suffix?: string;
  chip: string;
  iconCls: string;
}[] = [
  {
    key: 'totalAllTime',
    label: 'All-time inquiries',
    icon: BarChart3,
    chip: 'bg-sky-400/[0.22] border-sky-300/[0.42]',
    iconCls: 'text-sky-500 dark:text-sky-200',
  },
  {
    key: 'thisMonth',
    label: 'This month',
    icon: CalendarDays,
    chip: 'bg-teal-400/[0.22] border-teal-300/[0.42]',
    iconCls: 'text-teal-500 dark:text-teal-200',
  },
  {
    key: 'acceptanceRate',
    label: 'Acceptance rate',
    icon: TrendingUp,
    suffix: '%',
    chip: 'bg-emerald-400/[0.22] border-emerald-300/[0.42]',
    iconCls: 'text-emerald-500 dark:text-emerald-200',
  },
  {
    key: 'inProgress',
    label: 'In progress',
    icon: Play,
    chip: 'bg-pink-400/[0.22] border-pink-300/[0.42]',
    iconCls: 'text-pink-500 dark:text-pink-200',
  },
];

export const AnalyticsOverview = ({ analytics }: AnalyticsOverviewProps) => {
  return (
    <section className="space-y-3">
      <div>
        <p className="orbit-eyebrow">Analytics</p>
        <h2 className="font-editorial text-2xl md:text-3xl text-foreground dark:text-white leading-none mt-2">
          The long view
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 stagger-children">
        {analyticsCards.map((card) => {
          const Icon = card.icon;
          const value = analytics[card.key];

          return (
            <div
              key={card.key}
              className="group rounded-2xl border border-border/60 dark:border-white/[0.08] bg-card dark:bg-white/[0.06] dark:backdrop-blur-md orbit-box-dark p-4 transition-colors hover:border-foreground/30 dark:hover:border-white/[0.20]"
            >
              <div className="flex items-start justify-between gap-2">
                <span className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 border transition-transform group-hover:scale-110 ${card.chip}`}>
                  <Icon className={`w-4 h-4 ${card.iconCls}`} />
                </span>
                <span className="font-display text-2xl font-semibold tabular-nums text-foreground">
                  {value}
                  {card.suffix || ''}
                </span>
              </div>
              <p className="mt-3 text-sm font-medium text-foreground leading-tight">
                {card.label}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
};
